import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, TemplateRef, ViewChild, inject } from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  ValidationErrors,
  Validators
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { I18NService } from '@core';
import { I18nPipe, ALAIN_I18N_TOKEN } from '@delon/theme';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalModule, NzModalService } from 'ng-zorro-antd/modal';
import { NzRadioModule } from 'ng-zorro-antd/radio';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { EMPTY, auditTime, finalize, forkJoin, map, merge, of, startWith, Subscription, switchMap } from 'rxjs';
import { TestIdDirective } from 'src/app/shared/directives/test-id.directive';

import {
  BroadcastTaskDetail,
  BroadcastDeliveryResult,
  CustomerLabelListResult,
  CustomerLabelOption,
  LeadReasonOption,
  MatchLogic,
  PersonalBroadcastPayload,
  PersonalBroadcastService
} from '../personal-broadcast.service';

type PreviewMessage = {
  type: 'text' | 'image' | 'file' | 'template';
  typeLabel: string;
  text?: string;
  imageUrl?: string;
  fileName?: string;
  title?: string;
  subtitle?: string;
  buttonTexts?: string[];
};

type TemplateButtonFormValue = {
  text?: string;
  action?: 'link' | 'leadReason' | string;
  url?: string;
  leadReasonMode?: 'create' | 'select' | string;
  leadReasonId?: string;
  leadReasonName?: string;
  leadReasonSourceType?: string | null;
  leadReasonDescription?: string;
  leadReasonBizId?: string;
  leadReasonValidMode?: 'permanent' | 'dated' | string;
  leadReasonValidUntil?: Date | null;
};

const MIN_TEMPLATE_BUTTONS = 1;
const MAX_TEMPLATE_BUTTONS = 3;
const HTTP_URL_PATTERN = /^https?:\/\/.+/i;

const scheduledSendValidator = (control: AbstractControl): ValidationErrors | null => {
  const sendMode = control.get('sendMode')?.value;
  const sendAt = control.get('sendAt')?.value;
  if (sendMode === 'scheduled' && !sendAt) return { sendAtRequired: true };
  if (sendMode === 'scheduled' && sendAt) {
    const now = new Date();
    now.setSeconds(0, 0);
    now.setMinutes(now.getMinutes() + 10);
    const minSendAt = now;
    const sendAtDate = sendAt instanceof Date ? sendAt : new Date(sendAt);
    if (sendAtDate.getTime() < minSendAt.getTime()) return { sendAtTooSoon: true };
  }
  return null;
};

@Component({
  selector: 'personal-broadcast-detail',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    NzFormModule,
    NzInputModule,
    NzButtonModule,
    NzCardModule,
    NzCheckboxModule,
    NzIconModule,
    NzRadioModule,
    NzSelectModule,
    NzSpinModule,
    NzTagModule,
    NzTableModule,
    NzDatePickerModule,
    NzModalModule,
    I18nPipe,
    TestIdDirective
  ],
  templateUrl: './personal-broadcast-detail.component.html',
  styleUrls: ['./personal-broadcast-detail.component.less']
})
export class PersonalBroadcastDetailComponent implements OnInit, OnDestroy {
  private readonly fb = inject(FormBuilder);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly modal = inject(NzModalService);
  private readonly message = inject(NzMessageService);
  private readonly personalBroadcastService = inject(PersonalBroadcastService);
  private readonly i18n = inject<I18NService>(ALAIN_I18N_TOKEN);
  private readonly subscriptions = new Subscription();

  loading = false;
  submitting = false;
  canSend = false;
  tenantId = '';
  taskId = '';
  serviceNumberOpenType = '';
  serviceNumberId = '';
  returnUrl = '';
  previewMessages: PreviewMessage[] = [];
  deliveryResults: BroadcastDeliveryResult[] = [];
  deliveryTotal = 0;
  deliveryPageIndex = 1;
  deliveryPageSize = 20;
  deliveryLoading = false;
  deliveryStatusFilter = '';

  @ViewChild('sendConfirmTpl', { static: false }) sendConfirmTpl?: TemplateRef<unknown>;

  form = this.fb.group(
    {
      title: ['', [Validators.required, Validators.maxLength(100)]],
      remark: ['', [Validators.maxLength(500)]],
      channel: ['', [Validators.required]],
      labelIds: [[] as string[], [Validators.required]],
      labelMatchMode: ['or' as 'or' | 'and', [Validators.required]],
      sendMode: ['immediate', [Validators.required]],
      sendAt: [null as Date | null],
      messageEnabled: [true],
      messages: this.fb.array([this.createMessageItem()])
    },
    { validators: scheduledSendValidator }
  );

  messageTypes = [
    {
      label: this.i18n.fanyi('personal-broadcast.detail.message-type.text'),
      displayLabel: `📝 ${this.i18n.fanyi('personal-broadcast.detail.message-type.text')}`,
      value: 'text'
    },
    {
      label: this.i18n.fanyi('personal-broadcast.detail.message-type.template'),
      displayLabel: `🎯 ${this.i18n.fanyi('personal-broadcast.detail.message-type.template')}`,
      value: 'template'
    },
    {
      label: this.i18n.fanyi('personal-broadcast.detail.message-type.image'),
      displayLabel: `🖼️ ${this.i18n.fanyi('personal-broadcast.detail.message-type.image')}`,
      value: 'image'
    },
    {
      label: this.i18n.fanyi('personal-broadcast.detail.message-type.file'),
      displayLabel: `📎 ${this.i18n.fanyi('personal-broadcast.detail.message-type.file')}`,
      value: 'file'
    }
  ];
  channelOptions = [
    { label: this.i18n.fanyi('personal-broadcast.channel.preset'), value: 'Preset', iconType: 'star' },
    { label: 'LINE', value: 'Line', icon: 'assets/images/channels/icon-im-line.svg' },
    { label: 'Facebook', value: 'Facebook', icon: 'assets/images/channels/icon-im-fb.svg' },
    { label: 'Instagram', value: 'Instagram', icon: 'assets/images/channels/icon-im-ig.svg' },
    { label: this.i18n.fanyi('personal-broadcast.detail.channel.aiwow'), value: 'Aiwow', iconType: 'star', disabled: true }
  ];
  textVariables = [
    { token: '{customer}', label: this.i18n.fanyi('personal-broadcast.detail.text-variable.customer') },
    { token: '{nickname}', label: this.i18n.fanyi('personal-broadcast.detail.text-variable.nickname') }
  ];
  templateButtonActionOptions = [
    {
      label: this.i18n.fanyi('personal-broadcast.detail.button-action.option.link'),
      displayLabel: `🔗 ${this.i18n.fanyi('personal-broadcast.detail.button-action.option.link')}`,
      value: 'link'
    },
    {
      label: this.i18n.fanyi('personal-broadcast.detail.button-action.option.lead-reason'),
      displayLabel: `🎯 ${this.i18n.fanyi('personal-broadcast.detail.button-action.option.lead-reason')}`,
      value: 'leadReason'
    }
  ];
  leadReasonTypeOptions = [
    { labelI18nKey: 'servicenumber.inbound-reason.type.scan-qr-code', value: 'SCAN_QR_CODE' },
    { labelI18nKey: 'servicenumber.inbound-reason.type.product-consult', value: 'PRODUCT_CONSULT' },
    { labelI18nKey: 'servicenumber.inbound-reason.type.promotion-consult', value: 'PROMOTION_CONSULT' },
    { labelI18nKey: 'servicenumber.inbound-reason.type.signature', value: 'SIGNATURE' },
    { labelI18nKey: 'servicenumber.inbound-reason.type.customer-support', value: 'CUSTOMER_SUPPORT' },
    { labelI18nKey: 'servicenumber.inbound-reason.type.pricing-consult', value: 'PRICING_CONSULT' },
    { labelI18nKey: 'servicenumber.inbound-reason.type.custom', value: 'CUSTOM' }
  ];
  leadReasonOptions: LeadReasonOption[] = [];
  leadReasonLoading = false;
  leadReasonHasMore = true;
  leadReasonTotal = 0;
  leadReasonPageIndex = 0;
  leadReasonPageSize = 20;
  leadReasonKey = '';
  private leadReasonSearchTimer: number | null = null;
  private leadReasonCache = new Map<string, LeadReasonOption>();
  buttonActionConfigVisible = false;
  buttonActionConfigTitle = '';
  buttonActionConfigBtn?: FormGroup;
  buttonActionConfigWidth = 640;
  buttonActionConfigShowAdvanced = false;
  buttonActionConfigCreateVisible = false;

  labelOptions: CustomerLabelOption[] = [];
  selectedLabelIdSet = new Set<string>();
  estimatedRecipients = 0;
  labelLoading = false;
  labelSearchControl = this.fb.control('');
  labelPanelExpanded = false;
  labelMatchMode: 'or' | 'and' = 'or';
  labelHasNext = false;
  labelPageIndex = 0;
  labelPageSize = 50;
  labelRefreshTime = 0;
  labelLoadingMore = false;
  private labelSearchTimer: number | null = null;
  private activeAttachmentMessageIndex: number | null = null;
  private estimateRecipientsSub: Subscription | null = null;

  ngOnInit(): void {
    this.tenantId = this.route.snapshot.paramMap.get('id') || '';
    this.taskId = this.route.snapshot.paramMap.get('taskId') || '';
    this.serviceNumberOpenType = this.route.snapshot.queryParamMap.get('serviceNumberOpenType') || '';
    this.serviceNumberId = this.route.snapshot.queryParamMap.get('serviceNumberId') || '';
    this.returnUrl = this.normalizeReturnUrl(this.route.snapshot.queryParamMap.get('returnUrl') || '');
    this.configureTargetingValidation();
    this.subscriptions.add(
      this.form.statusChanges.pipe(startWith(this.form.status), auditTime(0)).subscribe(() => {
        this.updateCanSend();
        this.cdr.markForCheck();
      })
    );
    const labelIdsControl = this.form.get('labelIds');
    if (labelIdsControl) {
      this.subscriptions.add(
        labelIdsControl.valueChanges.subscribe(value => {
          const ids = (value || []) as string[];
          this.selectedLabelIdSet = new Set(ids);
          this.updateSelectedLabelsAndEstimate(ids);
          this.updateCanSend();
        })
      );
    }
    const labelMatchModeControl = this.form.get('labelMatchMode');
    if (labelMatchModeControl) {
      this.subscriptions.add(
        labelMatchModeControl.valueChanges.subscribe(mode => {
          this.labelMatchMode = (mode || 'or') as 'or' | 'and';
          this.updateSelectedLabelsAndEstimate(Array.from(this.selectedLabelIdSet));
        })
      );
    }
    if (this.requiresCustomerLabels) {
      this.labelRefreshTime = Date.now();
      this.loadCustomerLabels(true);
      this.subscriptions.add(
        this.labelSearchControl.valueChanges.subscribe(() => {
          if (this.labelSearchTimer) window.clearTimeout(this.labelSearchTimer);
          this.labelSearchTimer = window.setTimeout(() => {
            this.labelRefreshTime = Date.now();
            this.loadCustomerLabels(true);
          }, 300);
        })
      );
    }
    const sendModeControl = this.form.get('sendMode');
    const sendAtControl = this.form.get('sendAt');
    if (sendModeControl && sendAtControl) {
      this.subscriptions.add(
        sendModeControl.valueChanges.subscribe(mode => {
          if (mode !== 'scheduled') {
            sendAtControl.setValue(null);
          }
          this.updateCanSend();
          this.cdr.markForCheck();
        })
      );
    }
    this.setupMessageDerivedState();
    if (this.taskId) {
      this.loadBroadcastTaskDetail();
    }
    this.updateCanSend();
    this.cdr.markForCheck();
  }

  ngOnDestroy(): void {
    this.messagesArray.controls.forEach(control => {
      const imageUrl = String(control.get(['image', 'previewUrl'])?.value || '');
      const templateUrl = String(control.get(['templateImage', 'previewUrl'])?.value || '');
      this.revokeObjectUrlIfNeeded(imageUrl);
      this.revokeObjectUrlIfNeeded(templateUrl);
    });
    if (this.labelSearchTimer) window.clearTimeout(this.labelSearchTimer);
    this.subscriptions.unsubscribe();
  }

  get isCreate(): boolean {
    return !this.taskId;
  }

  get requiresCustomerLabels(): boolean {
    return this.serviceNumberOpenType !== 'Internal';
  }

  get requiresChannel(): boolean {
    return this.serviceNumberOpenType !== 'Internal';
  }

  get actionLabel(): string {
    const sendMode = String(this.form.get('sendMode')?.value || 'immediate') as 'immediate' | 'scheduled';
    if (this.isCreate) {
      return sendMode === 'scheduled'
        ? this.i18n.fanyi('personal-broadcast.detail.action.create.scheduled')
        : this.i18n.fanyi('personal-broadcast.detail.action.create.immediate');
    }
    return sendMode === 'scheduled'
      ? this.i18n.fanyi('personal-broadcast.detail.action.edit.scheduled')
      : this.i18n.fanyi('personal-broadcast.detail.action.edit.immediate');
  }

  get messageEnabled(): boolean {
    return Boolean(this.form.get('messageEnabled')?.value);
  }

  get selectedChannelLabel(): string {
    const channelValue = this.form.get('channel')?.value;
    return this.channelOptions.find(option => option.value === channelValue)?.label || '';
  }

  get estimatedRecipientsText(): string {
    if (!this.requiresCustomerLabels) return '—';
    const labelIds = (this.form.get('labelIds')?.value || []) as string[];
    if (labelIds.length === 0) return '0';
    return String(this.estimatedRecipients);
  }

  get selectedLabelNames(): string[] {
    return this.labelOptions.filter(item => this.selectedLabelIdSet.has(item.id)).map(item => item.name);
  }

  get sendTimeText(): string {
    const sendMode = this.form.get('sendMode')?.value;
    if (sendMode === 'scheduled') {
      const sendAt = this.form.get('sendAt')?.value as Date | null;
      if (!sendAt) return '—';
      return this.formatDateTime(sendAt);
    }
    return this.i18n.fanyi('personal-broadcast.detail.action.create.immediate');
  }

  disableSendAtDate = (current: Date): boolean => {
    const min = this.getMinScheduledSendAt();
    const currentDate = new Date(current);
    const minDate = new Date(min);
    currentDate.setHours(0, 0, 0, 0);
    minDate.setHours(0, 0, 0, 0);
    return currentDate.getTime() < minDate.getTime();
  };

  disableSendAtTime = (
    current: Date | Date[] | null
  ): { nzDisabledHours: () => number[]; nzDisabledMinutes: (hour: number) => number[]; nzDisabledSeconds: () => number[] } => {
    const min = this.getMinScheduledSendAt();
    const currentDate = Array.isArray(current) ? current[0] : current;
    const currentValue = currentDate ? new Date(currentDate) : new Date(min);
    const sameDay = this.isSameDate(currentValue, min);
    if (!sameDay) {
      return { nzDisabledHours: () => [], nzDisabledMinutes: () => [], nzDisabledSeconds: () => [] };
    }
    const minHour = min.getHours();
    const minMinute = min.getMinutes();
    const disabledHours = Array.from({ length: minHour }, (_, i) => i);
    const disabledMinutes = (hour: number) => {
      if (hour < minHour) return Array.from({ length: 60 }, (_, i) => i);
      if (hour === minHour) return Array.from({ length: minMinute }, (_, i) => i);
      return [];
    };
    return { nzDisabledHours: () => disabledHours, nzDisabledMinutes: disabledMinutes, nzDisabledSeconds: () => [] };
  };

  onSendClick(): void {
    this.form.markAllAsTouched();
    this.form.updateValueAndValidity();
    this.updateCanSend();
    this.cdr.markForCheck();
    if (this.requiresCustomerLabels && this.form.get('labelIds')?.invalid) {
      this.message.warning(this.i18n.fanyi('personal-broadcast.detail.validation.select-tags'));
      return;
    }
    if (this.requiresChannel && this.form.get('channel')?.invalid) {
      this.message.warning(this.i18n.fanyi('personal-broadcast.detail.validation.select-channel'));
      return;
    }
    const currentSendMode = String(this.form.get('sendMode')?.value || 'immediate') as 'immediate' | 'scheduled';
    if (this.requiresCustomerLabels && currentSendMode === 'immediate' && this.estimatedRecipients === 0) {
      this.message.warning(this.i18n.fanyi('personal-broadcast.detail.validation.no-recipients'));
      return;
    }
    if (this.messageEnabled) {
      const invalidIndex = this.validateMessages();
      if (invalidIndex !== -1) {
        this.message.warning(this.i18n.fanyi('personal-broadcast.detail.validation.complete-message', { index: invalidIndex + 1 }));
        return;
      }
    }
    if (this.form.hasError('sendAtRequired')) {
      this.message.warning(this.i18n.fanyi('personal-broadcast.detail.validation.select-send-at'));
      return;
    }
    if (this.form.hasError('sendAtTooSoon')) {
      this.message.warning(this.i18n.fanyi('personal-broadcast.detail.validation.send-at-too-soon'));
      return;
    }

    if (!this.sendConfirmTpl) {
      this.submit();
      return;
    }
    this.modal.confirm({
      nzTitle: this.isCreate
        ? String(this.form.get('sendMode')?.value || '') === 'scheduled'
          ? this.i18n.fanyi('personal-broadcast.detail.confirm.title.create.scheduled')
          : this.i18n.fanyi('personal-broadcast.detail.confirm.title.create.immediate')
        : String(this.form.get('sendMode')?.value || '') === 'scheduled'
          ? this.i18n.fanyi('personal-broadcast.detail.confirm.title.edit.scheduled')
          : this.i18n.fanyi('personal-broadcast.detail.confirm.title.edit.immediate'),
      nzContent: this.sendConfirmTpl,
      nzOkText: this.actionLabel,
      nzCancelText: this.i18n.fanyi('common.cancel'),
      nzOnOk: () => this.submit()
    });
  }

  private formatDateTime(value: Date): string {
    const date = value instanceof Date ? value : new Date(value);
    const pad2 = (n: number) => String(n).padStart(2, '0');
    return `${date.getFullYear()}/${pad2(date.getMonth() + 1)}/${pad2(date.getDate())} ${pad2(date.getHours())}:${pad2(date.getMinutes())}`;
  }

  private loadBroadcastTaskDetail(): void {
    if (!this.tenantId || !this.taskId) return;
    this.loading = true;
    this.cdr.markForCheck();
    this.personalBroadcastService
      .getBroadcastTaskDetail(this.tenantId, this.taskId, this.serviceNumberId)
      .pipe(
        finalize(() => {
          this.loading = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: detail => {
          if (!detail) return;
          this.applyBroadcastDetailToForm(detail);
          this.loadDeliveryResults();
          this.updateSelectedLabelsAndEstimate(Array.from(this.selectedLabelIdSet));
          this.updateCanSend();
          this.cdr.markForCheck();
        },
        error: () => {
          this.message.error(this.i18n.fanyi('personal-broadcast.detail.load-detail-failed'));
        }
      });
  }

  loadDeliveryResults(): void {
    if (!this.taskId) return;
    this.deliveryLoading = true;
    this.personalBroadcastService
      .listBroadcastDeliveryResults({
        broadcastId: this.taskId,
        pageIndex: this.deliveryPageIndex - 1,
        pageSize: this.deliveryPageSize,
        status: this.deliveryStatusFilter || undefined
      })
      .pipe(
        finalize(() => {
          this.deliveryLoading = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe(result => {
        this.deliveryResults = result.items;
        this.deliveryTotal = result.totalCount;
      });
  }

  onDeliveryPageIndexChange(pageIndex: number): void {
    this.deliveryPageIndex = pageIndex;
    this.loadDeliveryResults();
  }

  onDeliveryStatusChange(status: string): void {
    this.deliveryStatusFilter = status || '';
    this.deliveryPageIndex = 1;
    this.loadDeliveryResults();
  }

  deliveryStatusLabel(status: string): string {
    return ({ Pending: '待發送', Success: '成功', Failed: '失敗', Interrupted: '已中斷' } as Record<string, string>)[status] || status;
  }

  deliveryTime(value?: number): string {
    return value ? this.formatDateTime(new Date(value)) : '—';
  }

  private applyBroadcastDetailToForm(detail: BroadcastTaskDetail): void {
    this.form.get('title')?.setValue(detail.title || '', { emitEvent: false });
    this.form.get('remark')?.setValue(detail.remark || '', { emitEvent: false });
    this.form.get('channel')?.setValue(detail.channel || 'Preset', { emitEvent: false });
    this.form.get('labelIds')?.setValue(detail.labelIds || [], { emitEvent: false });
    const labelMatchMode: 'or' | 'and' = detail.matchLogic === MatchLogic.And ? 'and' : 'or';
    this.form.get('labelMatchMode')?.setValue(labelMatchMode, { emitEvent: false });
    this.labelMatchMode = labelMatchMode;
    this.selectedLabelIdSet = new Set(detail.labelIds || []);

    const broadcastTime = detail.broadcastTime;
    const scheduled = broadcastTime != null && Number.isFinite(broadcastTime) && broadcastTime > 0;
    this.form.get('sendMode')?.setValue(scheduled ? 'scheduled' : 'immediate', { emitEvent: false });
    this.form.get('sendAt')?.setValue(scheduled ? new Date(broadcastTime) : null, { emitEvent: false });

    const messages = this.parseBroadcastContent(detail.content || []);
    const messageEnabled = messages.length > 0;
    this.form.get('messageEnabled')?.setValue(messageEnabled, { emitEvent: false });

    const arr = this.messagesArray;
    while (arr.length) arr.removeAt(0);
    if (!messageEnabled) {
      arr.push(this.createMessageItem('text'));
      return;
    }
    messages.forEach(msg => {
      const grp = this.createMessageItem(msg.type);
      grp.get('type')?.setValue(msg.type, { emitEvent: false });
      if (msg.type === 'text') {
        grp.get(['text', 'content'])?.setValue(msg.content || '', { emitEvent: false });
      } else if (msg.type === 'image') {
        grp.get(['image', 'name'])?.setValue(msg.name || '', { emitEvent: false });
        grp.get(['image', 'size'])?.setValue(msg.size || 0, { emitEvent: false });
        grp.get(['image', 'fileId'])?.setValue(msg.fileId || '', { emitEvent: false });
        grp.get(['image', 'url'])?.setValue(msg.url || '', { emitEvent: false });
        grp.get(['image', 'thumbnailUrl'])?.setValue(msg.thumbnailUrl || '', { emitEvent: false });
        grp.get(['image', 'previewUrl'])?.setValue(msg.url || msg.thumbnailUrl || '', { emitEvent: false });
      } else if (msg.type === 'file') {
        const nameFromUrl = this.extractFileNameFromUrl(msg.url || '');
        grp.get(['file', 'name'])?.setValue(msg.name || nameFromUrl || '', { emitEvent: false });
        grp.get(['file', 'size'])?.setValue(msg.size || 0, { emitEvent: false });
        grp.get(['file', 'type'])?.setValue(msg.mimeType || '', { emitEvent: false });
        grp.get(['file', 'fileId'])?.setValue(msg.fileId || '', { emitEvent: false });
        grp.get(['file', 'url'])?.setValue(msg.url || '', { emitEvent: false });
      } else if (msg.type === 'template') {
        grp.get(['template', 'title'])?.setValue(msg.title || '', { emitEvent: false });
        grp.get(['template', 'subtitle'])?.setValue(msg.subtitle || '', { emitEvent: false });
        grp.get(['templateImage', 'url'])?.setValue(msg.imageUrl || '', { emitEvent: false });
        grp.get(['templateImage', 'previewUrl'])?.setValue(msg.imageUrl || '', { emitEvent: false });
        const buttons = grp.get(['template', 'buttons']) as FormArray<FormGroup>;
        (msg.buttons || []).forEach(btn => {
          const action = btn.action || 'link';
          const btnGrp = this.createTemplateButtonForm({
            text: btn.text || '',
            action,
            url: action === 'link' ? (btn as any).url || '' : '',
            leadReasonMode: 'select',
            leadReasonId: action === 'leadReason' ? (btn as any).leadReasonId || '' : '',
            leadReasonName: action === 'leadReason' ? (btn as any).leadReasonName || '' : ''
          });
          buttons.push(btnGrp);
        });
      }
      arr.push(grp);
    });
    this.refreshMessageDerivedState();
  }

  private extractFileNameFromUrl(url: string): string {
    const value = String(url || '').trim();
    if (!value) return '';
    try {
      const pathname = value.startsWith('http') ? new URL(value).pathname : value;
      const last = pathname.split('/').filter(Boolean).pop() || '';
      return decodeURIComponent(last);
    } catch {
      const last = value.split('/').filter(Boolean).pop() || '';
      return last;
    }
  }

  private parseBroadcastContent(content: Array<{ index?: number; type?: string; content?: any }>): Array<
    | { type: 'text'; content: string }
    | { type: 'image'; name: string; size: number; fileId: string; url?: string; thumbnailUrl?: string }
    | { type: 'file'; name: string; size: number; mimeType: string; fileId: string; url?: string }
    | {
        type: 'template';
        title: string;
        subtitle: string;
        imageUrl: string;
        buttons: Array<
          | { text: string; action: 'link'; url: string }
          | { text: string; action: 'leadReason'; leadReasonId: string; leadReasonName: string }
        >;
      }
  > {
    const items = (content || [])
      .slice()
      .sort((a, b) => Number(a?.index || 0) - Number(b?.index || 0))
      .map(raw => this.parseBroadcastContentItem(raw))
      .filter(Boolean) as any[];
    return items;
  }

  private parseBroadcastContentItem(raw: { type?: string; content?: any }):
    | { type: 'text'; content: string }
    | { type: 'image'; name: string; size: number; fileId: string; url?: string; thumbnailUrl?: string }
    | { type: 'file'; name: string; size: number; mimeType: string; fileId: string; url?: string }
    | {
        type: 'template';
        title: string;
        subtitle: string;
        imageUrl: string;
        buttons: Array<
          | { text: string; action: 'link'; url: string }
          | { text: string; action: 'leadReason'; leadReasonId: string; leadReasonName: string }
        >;
      }
    | null {
    const rawType = String(raw?.type || '').toLowerCase();
    const contentRaw = raw?.content;
    const contentObj = this.safeJsonParse(contentRaw);
    if (rawType.includes('text') || String(contentObj?.type || '').toLowerCase() === 'text') {
      const text = String(contentObj?.text ?? contentObj?.content ?? contentRaw ?? '').trim();
      return { type: 'text', content: text };
    }
    if (rawType.includes('image') || String(contentObj?.type || '').toLowerCase() === 'image') {
      const fileId = String(contentObj?.fileId ?? contentObj?.id ?? '').trim();
      const name = String(contentObj?.name ?? '').trim();
      const size = Number(contentObj?.size ?? 0);
      const url = String(contentObj?.url ?? '').trim() || undefined;
      const thumbnailUrl = String(contentObj?.thumbnailUrl ?? '').trim() || undefined;
      return { type: 'image', name, size: Number.isFinite(size) ? size : 0, fileId, url, thumbnailUrl };
    }
    if (rawType.includes('file') || String(contentObj?.type || '').toLowerCase() === 'file') {
      const fileId = String(contentObj?.fileId ?? contentObj?.id ?? '').trim();
      const name = String(contentObj?.name ?? '').trim();
      const size = Number(contentObj?.size ?? 0);
      const mimeType = String(contentObj?.mimeType ?? contentObj?.type ?? '').trim();
      const url = String(contentObj?.url ?? '').trim() || undefined;
      return { type: 'file', name, size: Number.isFinite(size) ? size : 0, mimeType, fileId, url };
    }
    if (rawType.includes('template') || String(contentObj?.type || '').toLowerCase() === 'carousel') {
      const title = String(contentObj?.title ?? '').trim();
      const subtitle = String(contentObj?.subTitle ?? contentObj?.subtitle ?? '').trim();
      const imageUrl = String(contentObj?.imageUrl ?? '').trim();
      const actions = Array.isArray(contentObj?.actions) ? contentObj.actions : [];
      const buttons = actions
        .map((a: any) => {
          const label = String(a?.label ?? a?.text ?? '').trim();
          const actionType = String(a?.type ?? '').toLowerCase();
          if (actionType === 'url' || a?.url) {
            return { text: label, action: 'link' as const, url: String(a?.url ?? '').trim() };
          }
          const dataObj = this.safeJsonParse(a?.data);
          const sourceId = String(dataObj?.sourceId ?? dataObj?.leadReasonId ?? '').trim();
          return { text: label, action: 'leadReason' as const, leadReasonId: sourceId, leadReasonName: '' };
        })
        .filter((b: any) => Boolean(b?.text));
      return { type: 'template', title, subtitle, imageUrl, buttons };
    }
    return null;
  }

  private safeJsonParse(input: any): any {
    if (input == null) return null;
    if (typeof input === 'object') return input;
    const str = String(input || '').trim();
    if (!str) return null;
    try {
      return JSON.parse(str);
    } catch {
      return null;
    }
  }

  private getMinScheduledSendAt(): Date {
    const now = new Date();
    now.setSeconds(0, 0);
    now.setMinutes(now.getMinutes() + 10);
    return now;
  }

  private isSameDate(a: Date, b: Date): boolean {
    return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
  }

  back(): void {
    if (this.returnUrl) {
      this.router.navigateByUrl(this.returnUrl);
      return;
    }
    this.router.navigate(['/personal-broadcast-list', this.tenantId]);
  }

  private normalizeReturnUrl(value: string): string {
    const url = String(value || '').trim();
    return url.startsWith('/') && !url.startsWith('//') ? url : '';
  }

  private configureTargetingValidation(): void {
    const labelIdsControl = this.form.get('labelIds');
    if (labelIdsControl) {
      labelIdsControl.setValidators(this.requiresCustomerLabels ? [Validators.required] : []);
      if (!this.requiresCustomerLabels) labelIdsControl.setValue([], { emitEvent: false });
      labelIdsControl.updateValueAndValidity({ emitEvent: false });
    }
    const channelControl = this.form.get('channel');
    if (channelControl) {
      channelControl.setValidators(this.requiresChannel ? [Validators.required] : []);
      if (!this.requiresChannel) channelControl.setValue('', { emitEvent: false });
      channelControl.updateValueAndValidity({ emitEvent: false });
    }
  }

  private getBroadcastLabelIds(labelIds: string[]): string[] {
    return this.requiresCustomerLabels ? labelIds : [];
  }

  private getBroadcastChannel(channel: string): string | undefined {
    return this.requiresChannel ? channel : undefined;
  }

  deleteTask(): void {
    if (this.isCreate) return;
    const id = String(this.taskId || '').trim();
    if (!id) return;
    this.modal.confirm({
      nzTitle: this.i18n.fanyi('personal-broadcast.list.confirm.delete-title'),
      nzContent: this.i18n.fanyi('personal-broadcast.list.confirm.delete-content'),
      nzOkDanger: true,
      nzOkText: this.i18n.fanyi('personal-broadcast.list.confirm.delete-ok'),
      nzCancelText: this.i18n.fanyi('common.cancel'),
      nzOnOk: () => {
        this.loading = true;
        this.cdr.markForCheck();
        this.personalBroadcastService
          .deleteBroadcastTask(id)
          .pipe(
            finalize(() => {
              this.loading = false;
              this.cdr.markForCheck();
            })
          )
          .subscribe({
            next: () => {
              this.message.success(this.i18n.fanyi('personal-broadcast.list.message.delete-success'));
              this.back();
            },
            error: (err: any) => {
              const msg = String(err?.message || '').trim();
              this.message.error(
                msg
                  ? this.i18n.fanyi('personal-broadcast.list.message.delete-failed-with-msg', { msg })
                  : this.i18n.fanyi('personal-broadcast.list.message.delete-failed')
              );
            }
          });
      }
    });
  }

  get messagesArray(): FormArray<FormGroup> {
    return this.form.get('messages') as FormArray<FormGroup>;
  }

  addMessage(type: 'text' | 'image' | 'file' | 'template' = 'text'): void {
    if (this.messagesArray.length >= 5) return;
    this.messagesArray.push(this.createMessageItem(type));
    this.refreshMessageDerivedState();
    this.cdr.markForCheck();
  }

  removeMessage(index: number): void {
    if (this.messagesArray.length <= 1) return;
    const group = this.messagesArray.at(index);
    if (group) {
      const imageUrl = String(group.get(['image', 'previewUrl'])?.value || '');
      const templateUrl = String(group.get(['templateImage', 'previewUrl'])?.value || '');
      this.revokeObjectUrlIfNeeded(imageUrl);
      this.revokeObjectUrlIfNeeded(templateUrl);
    }
    this.messagesArray.removeAt(index);
    this.refreshMessageDerivedState();
  }

  trackByIndex(index: number): number {
    return index;
  }

  private setupMessageDerivedState(): void {
    const messagesControl = this.form.get('messages');
    const messageEnabledControl = this.form.get('messageEnabled');
    this.subscriptions.add(
      merge(messagesControl?.valueChanges || EMPTY, messageEnabledControl?.valueChanges || EMPTY)
        .pipe(startWith(null), auditTime(0))
        .subscribe(() => {
          this.refreshMessageDerivedState();
        })
    );
    this.refreshMessageDerivedState();
  }

  private buildPreviewMessages(): PreviewMessage[] {
    if (!this.messageEnabled) return [];
    return this.messagesArray.controls.map(control => {
      const typeValue = String(control.get('type')?.value || 'text');
      const typeLabel =
        this.messageTypes.find(type => type.value === typeValue)?.label || this.i18n.fanyi('personal-broadcast.detail.message-type.text');
      if (typeValue === 'text') {
        const text = String(control.get(['text', 'content'])?.value || '').trim();
        return { type: 'text', typeLabel, text };
      }
      if (typeValue === 'image') {
        const imageUrl = String(control.get(['image', 'previewUrl'])?.value || '');
        const fileName = String(control.get(['image', 'name'])?.value || '').trim();
        return { type: 'image', typeLabel, imageUrl, fileName };
      }
      if (typeValue === 'file') {
        const fileName = String(control.get(['file', 'name'])?.value || '').trim();
        return { type: 'file', typeLabel, fileName };
      }
      const title = String(control.get(['template', 'title'])?.value || '').trim();
      const subtitle = String(control.get(['template', 'subtitle'])?.value || '').trim();
      const imageUrl = String(control.get(['templateImage', 'previewUrl'])?.value || '');
      const buttons = control.get(['template', 'buttons']) as FormArray<FormGroup> | null;
      const buttonTexts = (buttons?.controls || []).map(btn => String(btn.get('text')?.value || '').trim()).filter(Boolean);
      return { type: 'template', typeLabel, title, subtitle, imageUrl, buttonTexts };
    });
  }

  private updateCanSend(): void {
    if (!this.form.valid) {
      this.canSend = false;
      return;
    }
    if (this.requiresCustomerLabels && this.selectedLabelIdSet.size === 0) {
      this.canSend = false;
      return;
    }
    if (this.submitting) {
      this.canSend = false;
      return;
    }
    const mode = String(this.form.get('sendMode')?.value || 'immediate') as 'immediate' | 'scheduled';
    if (this.requiresCustomerLabels && mode === 'immediate' && this.estimatedRecipients === 0) {
      this.canSend = false;
      return;
    }
    if (!this.messageEnabled) {
      this.canSend = true;
      return;
    }
    this.canSend = this.validateMessages() === -1;
  }

  private refreshMessageDerivedState(): void {
    this.previewMessages = this.buildPreviewMessages();
    this.updateCanSend();
    this.cdr.markForCheck();
  }

  get filteredLabelOptions(): CustomerLabelOption[] {
    return this.labelOptions;
  }

  toggleLabelPanel(): void {
    this.labelPanelExpanded = !this.labelPanelExpanded;
    this.cdr.markForCheck();
  }

  clearSelectedLabels(): void {
    this.form.get('labelIds')?.setValue([]);
    this.form.get('labelIds')?.markAsDirty();
    this.form.get('labelIds')?.updateValueAndValidity();
    this.cdr.markForCheck();
  }

  onLabelMatchModeChange(mode: 'or' | 'and'): void {
    this.labelMatchMode = mode;
    this.updateSelectedLabelsAndEstimate(Array.from(this.selectedLabelIdSet));
  }

  onLabelCheckedChange(label: CustomerLabelOption, checked: boolean): void {
    if (checked) this.selectedLabelIdSet.add(label.id);
    else this.selectedLabelIdSet.delete(label.id);
    this.form.get('labelIds')?.setValue(Array.from(this.selectedLabelIdSet));
  }

  toggleLabel(label: CustomerLabelOption): void {
    if (this.selectedLabelIdSet.has(label.id)) this.selectedLabelIdSet.delete(label.id);
    else this.selectedLabelIdSet.add(label.id);
    this.form.get('labelIds')?.setValue(Array.from(this.selectedLabelIdSet));
  }

  onLabelPanelScroll(event: Event): void {
    if (this.labelLoadingMore || this.labelLoading || !this.labelHasNext) return;
    const target = event.target as HTMLElement | null;
    if (!target) return;
    if (target.scrollTop + target.clientHeight >= target.scrollHeight - 24) {
      this.loadCustomerLabels(false);
    }
  }

  getMessageTypeLabel(type: unknown): string {
    const value = String(type || '');
    return this.messageTypes.find(t => t.value === value)?.label || '—';
  }

  toggleMessageCollapse(index: number): void {
    const group = this.messagesArray.at(index);
    if (!group) return;
    const collapsedControl = group.get('collapsed');
    collapsedControl?.setValue(!Boolean(collapsedControl.value));
  }

  insertTextVariable(textarea: HTMLTextAreaElement, group: FormGroup, token: string): void {
    const control = group.get(['text', 'content']);
    if (!control) return;
    const rawValue = String(control.value || '');
    const start = textarea.selectionStart ?? rawValue.length;
    const end = textarea.selectionEnd ?? rawValue.length;
    const nextValue = `${rawValue.slice(0, start)}${token}${rawValue.slice(end)}`;
    control.setValue(nextValue);
    requestAnimationFrame(() => {
      textarea.focus();
      const cursor = start + token.length;
      textarea.setSelectionRange(cursor, cursor);
    });
  }

  onSelectAttachment(group: FormGroup, event: Event): void {
    const input = event.target as HTMLInputElement | null;
    const file = input?.files?.[0];
    if (!file) return;
    group.get(['file', 'name'])?.setValue(file.name);
    group.get(['file', 'size'])?.setValue(file.size);
    group.get(['file', 'type'])?.setValue(file.type || '');
    group.get(['file', 'rawFile'])?.setValue(file);
    group.get(['file', 'fileId'])?.setValue('');
    group.get(['file', 'url'])?.setValue('');
    if (input) input.value = '';
    this.refreshMessageDerivedState();
  }

  openAttachmentPicker(messageIndex: number, input: HTMLInputElement): void {
    if (!this.messageEnabled) return;
    this.activeAttachmentMessageIndex = messageIndex;
    this.openNativeFilePicker(input);
  }

  onSelectAttachmentFromPicker(event: Event): void {
    const index = this.activeAttachmentMessageIndex;
    this.activeAttachmentMessageIndex = null;
    if (index === null) return;
    const group = this.messagesArray.at(index);
    if (!group) return;
    this.onSelectAttachment(group, event);
  }

  onSelectImageFile(group: FormGroup, event: Event, target: 'image' | 'templateImage'): void {
    const input = event.target as HTMLInputElement | null;
    const file = input?.files?.[0];
    if (!file) return;
    const maxBytes = 25 * 1024 * 1024;
    if (file.size > maxBytes) {
      this.message.error(this.i18n.fanyi('personal-broadcast.detail.upload.image-size-limit'));
      input.value = '';
      return;
    }
    if (!String(file.type || '').startsWith('image/')) {
      this.message.error(this.i18n.fanyi('personal-broadcast.detail.upload.image-type-invalid'));
      input.value = '';
      return;
    }
    group.get([target, 'name'])?.setValue(file.name);
    group.get([target, 'size'])?.setValue(file.size);
    group.get([target, 'rawFile'])?.setValue(file);
    group.get([target, 'fileId'])?.setValue('');
    group.get([target, 'url'])?.setValue('');
    if (target === 'image') group.get([target, 'thumbnailUrl'])?.setValue('');
    const prevUrl = String(group.get([target, 'previewUrl'])?.value || '');
    this.revokeObjectUrlIfNeeded(prevUrl);
    const objectUrl = URL.createObjectURL(file);
    group.get([target, 'previewUrl'])?.setValue(objectUrl);
    this.refreshMessageDerivedState();
    if (input) input.value = '';
  }

  addTemplateButton(group: FormGroup): void {
    const buttons = group.get(['template', 'buttons']) as FormArray<FormGroup> | null;
    if (!buttons) return;
    if (buttons.length >= MAX_TEMPLATE_BUTTONS) return;
    const button = this.createTemplateButtonForm();
    buttons.push(button);
    this.openButtonActionConfig(button, buttons.length - 1);
  }

  setLeadReasonMode(button: FormGroup, mode: 'create' | 'select'): void {
    button.get('leadReasonMode')?.setValue(mode);
    if (mode === 'create') {
      button.get('leadReasonId')?.setValue('');
      this.setLeadReasonFieldsDisabled(button, false);
    } else {
      this.applyLeadReasonSelectionToButton(button);
    }
    this.cdr.markForCheck();
  }

  createLeadReason(button: FormGroup): void {
    const name = String(button.get('leadReasonName')?.value || '').trim();
    if (!name) {
      this.message.warning(this.i18n.fanyi('personal-broadcast.detail.lead-reason.name-required'));
      return;
    }
    const sourceType = String(button.get('leadReasonSourceType')?.value || '').trim();
    if (!sourceType) {
      this.message.warning(this.i18n.fanyi('personal-broadcast.detail.lead-reason.type-required'));
      return;
    }
    const description = String(button.get('leadReasonDescription')?.value || '').trim();
    const bizId = String(button.get('leadReasonBizId')?.value || '').trim();
    const validMode = String(button.get('leadReasonValidMode')?.value || 'permanent') as 'permanent' | 'dated';
    const validUntilDate = button.get('leadReasonValidUntil')?.value as Date | null;
    const validUntil = validMode === 'dated' && validUntilDate ? new Date(validUntilDate).toISOString() : null;
    if (validMode === 'dated') {
      if (!validUntilDate) {
        this.message.warning(this.i18n.fanyi('personal-broadcast.detail.lead-reason.date-required'));
        return;
      }
      const selected = new Date(validUntilDate);
      const today = new Date();
      selected.setHours(0, 0, 0, 0);
      today.setHours(0, 0, 0, 0);
      if (selected.getTime() <= today.getTime()) {
        this.message.warning(this.i18n.fanyi('personal-broadcast.detail.lead-reason.date-must-future'));
        return;
      }
    }
    const validityPeriod = validMode === 'dated' && validUntilDate ? new Date(validUntilDate).getTime() : 0;
    this.subscriptions.add(
      this.personalBroadcastService
        .saveInboundReason({
          tenantId: this.tenantId,
          sourceName: name,
          sourceType,
          description: description || undefined,
          businessId: bizId || undefined,
          validityPeriod
        })
        .subscribe(success => {
          if (!success) {
            this.message.error(this.i18n.fanyi('personal-broadcast.detail.lead-reason.create-failed'));
            return;
          }
          this.personalBroadcastService
            .listInboundReasons({
              tenantId: this.tenantId,
              pageIndex: 0,
              pageSize: this.leadReasonPageSize,
              key: name,
              status: 'Enable'
            })
            .subscribe(res => {
              const matched = (res.items || []).find(item => item.name === name && item.sourceType === sourceType) || (res.items || [])[0];
              if (matched && matched.id) {
                this.leadReasonCache.set(matched.id, matched);
                this.leadReasonOptions = [matched, ...this.leadReasonOptions.filter(r => r.id !== matched.id)];
                button.get('leadReasonId')?.setValue(matched.id);
              }
              button.get('leadReasonMode')?.setValue('select');
              this.buttonActionConfigCreateVisible = false;
              this.buttonActionConfigShowAdvanced = false;
              button.get('leadReasonName')?.setValue('', { emitEvent: false });
              button.get('leadReasonSourceType')?.setValue(null, { emitEvent: false });
              button.get('leadReasonDescription')?.setValue('', { emitEvent: false });
              button.get('leadReasonBizId')?.setValue('', { emitEvent: false });
              button.get('leadReasonValidMode')?.setValue('permanent', { emitEvent: false });
              button.get('leadReasonValidUntil')?.setValue(null, { emitEvent: false });
              this.cdr.markForCheck();
            });
        })
    );
  }

  openButtonActionConfig(button: FormGroup, index: number): void {
    this.syncTemplateButtonValidators(button);
    this.buttonActionConfigBtn = button;
    this.buttonActionConfigTitle = this.i18n.fanyi('personal-broadcast.detail.button-action.modal-title', { index: index + 1 });
    const action = String(button.get('action')?.value || 'link');
    this.buttonActionConfigWidth = action === 'leadReason' ? 720 : 640;
    this.buttonActionConfigShowAdvanced = false;
    this.buttonActionConfigCreateVisible = false;
    if (action === 'leadReason') {
      button.get('leadReasonMode')?.setValue('select', { emitEvent: false });
      this.resetLeadReasonList();
      const selectedId = String(button.get('leadReasonId')?.value || '');
      if (selectedId) this.ensureLeadReasonItemLoaded(selectedId);
    }
    this.buttonActionConfigVisible = true;
    this.cdr.markForCheck();
  }

  closeButtonActionConfig(): void {
    this.buttonActionConfigVisible = false;
    this.buttonActionConfigBtn = undefined;
    this.buttonActionConfigCreateVisible = false;
    this.cdr.markForCheck();
  }

  toggleButtonActionAdvanced(): void {
    this.buttonActionConfigShowAdvanced = !this.buttonActionConfigShowAdvanced;
    this.cdr.markForCheck();
  }

  toggleLeadReasonCreate(): void {
    const btn = this.buttonActionConfigBtn;
    if (!btn) return;
    this.buttonActionConfigCreateVisible = !this.buttonActionConfigCreateVisible;
    this.buttonActionConfigShowAdvanced = false;
    if (this.buttonActionConfigCreateVisible) {
      btn.get('leadReasonName')?.setValue('', { emitEvent: false });
      btn.get('leadReasonSourceType')?.setValue(null, { emitEvent: false });
      btn.get('leadReasonDescription')?.setValue('', { emitEvent: false });
      btn.get('leadReasonBizId')?.setValue('', { emitEvent: false });
      btn.get('leadReasonValidMode')?.setValue('permanent', { emitEvent: false });
      btn.get('leadReasonValidUntil')?.setValue(null, { emitEvent: false });
    }
    this.cdr.markForCheck();
  }

  leadReasonTypeLabelI18nKey(type: string | null | undefined): string {
    const normalized = String(type || '').trim();
    return this.leadReasonTypeOptions.find(opt => opt.value === normalized)?.labelI18nKey || normalized;
  }

  onTemplateButtonActionChanged(button: FormGroup): void {
    const action = String(button.get('action')?.value || 'link');
    this.syncTemplateButtonValidators(button);
    this.buttonActionConfigWidth = action === 'leadReason' ? 720 : 640;
    if (action === 'leadReason') {
      button.get('leadReasonMode')?.setValue('select', { emitEvent: false });
      this.buttonActionConfigCreateVisible = false;
      this.buttonActionConfigShowAdvanced = false;
    }
    this.cdr.markForCheck();
  }

  getSelectedLeadReason(button: FormGroup): LeadReasonOption | null {
    const selectedId = String(button.get('leadReasonId')?.value || '');
    return this.leadReasonCache.get(selectedId) || this.leadReasonOptions.find(r => r.id === selectedId) || null;
  }

  getTemplateButtonActionSummary(button: FormGroup): string {
    const action = String(button.get('action')?.value || 'link');
    if (action === 'link') {
      const url = String(button.get('url')?.value || '').trim();
      return url
        ? this.i18n.fanyi('personal-broadcast.detail.button-action.summary.link', { url })
        : this.i18n.fanyi('personal-broadcast.detail.button-action.summary.link-empty');
    }
    const selectedId = String(button.get('leadReasonId')?.value || '');
    if (!selectedId) return this.i18n.fanyi('personal-broadcast.detail.button-action.summary.lead-reason-empty');
    const selected = this.leadReasonCache.get(selectedId) || this.leadReasonOptions.find(r => r.id === selectedId);
    return selected
      ? this.i18n.fanyi('personal-broadcast.detail.button-action.summary.lead-reason', { name: selected.name })
      : this.i18n.fanyi('personal-broadcast.detail.button-action.summary.lead-reason-missing');
  }

  getTemplateButtonText(button: FormGroup): string {
    return String(button.get('text')?.value || '').trim();
  }

  isButtonActionConfigInvalid(): boolean {
    const btn = this.buttonActionConfigBtn;
    if (!btn) return false;
    return btn.invalid;
  }

  showButtonConfigControlError(controlName: string, errorKey?: string): boolean {
    const btn = this.buttonActionConfigBtn;
    const control = btn?.get(controlName);
    if (!control) return false;
    if (!control.touched && !control.dirty) return false;
    return errorKey ? control.hasError(errorKey) : control.invalid;
  }

  handleButtonActionConfigOk(): void {
    const btn = this.buttonActionConfigBtn;
    if (!btn) {
      this.closeButtonActionConfig();
      return;
    }
    btn.markAllAsTouched();
    this.syncTemplateButtonValidators(btn);
    if (btn.invalid) {
      this.cdr.markForCheck();
      return;
    }
    this.closeButtonActionConfig();
  }

  onLeadReasonSelected(button: FormGroup): void {
    const selectedId = String(button.get('leadReasonId')?.value || '');
    if (selectedId) this.ensureLeadReasonItemLoaded(selectedId);
    this.cdr.markForCheck();
  }

  onLeadReasonSearch(value: string): void {
    const nextKey = String(value || '').trim();
    this.leadReasonKey = nextKey;
    if (this.leadReasonSearchTimer) window.clearTimeout(this.leadReasonSearchTimer);
    this.leadReasonSearchTimer = window.setTimeout(() => {
      this.resetLeadReasonList(nextKey);
      this.cdr.markForCheck();
    }, 300);
  }

  onLeadReasonScrollToBottom(): void {
    if (this.leadReasonLoading || !this.leadReasonHasMore) return;
    this.leadReasonPageIndex += 1;
    this.loadLeadReasonPage(true);
  }

  private resetLeadReasonList(key: string = this.leadReasonKey): void {
    this.leadReasonKey = String(key || '').trim();
    this.leadReasonPageIndex = 0;
    this.leadReasonHasMore = true;
    this.leadReasonTotal = 0;
    this.leadReasonOptions = [];
    this.loadLeadReasonPage(false);
  }

  private loadLeadReasonPage(append: boolean): void {
    this.leadReasonLoading = true;
    this.subscriptions.add(
      this.personalBroadcastService
        .listInboundReasons({
          tenantId: this.tenantId,
          pageIndex: this.leadReasonPageIndex,
          pageSize: this.leadReasonPageSize,
          key: this.leadReasonKey || undefined,
          status: 'Enable'
        })
        .pipe(
          finalize(() => {
            this.leadReasonLoading = false;
            this.cdr.markForCheck();
          })
        )
        .subscribe(res => {
          const nextItems = res.items || [];
          nextItems.forEach(item => {
            if (item?.id) this.leadReasonCache.set(item.id, item);
          });
          this.leadReasonTotal = res.total || 0;
          const merged = append ? [...this.leadReasonOptions, ...nextItems] : [...nextItems];
          const uniq = new Map<string, LeadReasonOption>();
          merged.forEach(item => {
            if (!item?.id) return;
            uniq.set(item.id, item);
          });
          this.leadReasonOptions = Array.from(uniq.values());
          this.leadReasonHasMore = this.leadReasonOptions.length < this.leadReasonTotal;
        })
    );
  }

  private ensureLeadReasonItemLoaded(id: string): void {
    const resolvedId = String(id || '').trim();
    if (!resolvedId) return;
    if (this.leadReasonCache.has(resolvedId)) return;
    this.subscriptions.add(
      this.personalBroadcastService.getInboundReasonItem(this.tenantId, resolvedId).subscribe(item => {
        if (!item || !item.id) return;
        this.leadReasonCache.set(item.id, item);
        if (!this.leadReasonOptions.some(r => r.id === item.id)) {
          this.leadReasonOptions = [item, ...this.leadReasonOptions];
        }
        this.cdr.markForCheck();
      })
    );
  }

  private applyLeadReasonSelectionToButton(button: FormGroup): void {
    const selectedId = String(button.get('leadReasonId')?.value || '');
    const item = this.leadReasonOptions.find(r => r.id === selectedId);
    const isSelectMode = button.get('leadReasonMode')?.value === 'select';
    if (!item) {
      this.setLeadReasonFieldsDisabled(button, isSelectMode);
      return;
    }
    button.get('leadReasonName')?.setValue(item.name, { emitEvent: false });
    button.get('leadReasonSourceType')?.setValue(item.sourceType, { emitEvent: false });
    button.get('leadReasonDescription')?.setValue(item.description, { emitEvent: false });
    button.get('leadReasonBizId')?.setValue(item.bizId, { emitEvent: false });
    button.get('leadReasonValidMode')?.setValue(item.validMode, { emitEvent: false });
    button.get('leadReasonValidUntil')?.setValue(item.validUntil ? new Date(item.validUntil) : null, { emitEvent: false });
    this.setLeadReasonFieldsDisabled(button, isSelectMode);
  }

  private setLeadReasonFieldsDisabled(button: FormGroup, disabled: boolean): void {
    const keys = [
      'leadReasonName',
      'leadReasonSourceType',
      'leadReasonDescription',
      'leadReasonBizId',
      'leadReasonValidMode',
      'leadReasonValidUntil'
    ];
    keys.forEach(key => {
      const control = button.get(key);
      if (!control) return;
      if (disabled) control.disable({ emitEvent: false });
      else control.enable({ emitEvent: false });
    });
  }

  removeTemplateButton(group: FormGroup, index: number): void {
    const buttons = group.get(['template', 'buttons']) as FormArray<FormGroup> | null;
    if (!buttons) return;
    buttons.removeAt(index);
  }

  getTemplateButtons(group: FormGroup): FormArray<FormGroup> {
    return group.get(['template', 'buttons']) as FormArray<FormGroup>;
  }

  openNativeFilePicker(input: HTMLInputElement): void {
    if (!this.messageEnabled) return;
    input.value = '';
    input.click();
  }

  private revokeObjectUrlIfNeeded(url: string): void {
    if (!url) return;
    if (!url.startsWith('blob:')) return;
    try {
      URL.revokeObjectURL(url);
    } catch {}
  }

  private createMessageItem(type: 'text' | 'image' | 'file' | 'template' = 'text'): FormGroup {
    return this.fb.group({
      collapsed: [false],
      type: [type, [Validators.required]],
      text: this.fb.group({
        content: ['', [Validators.maxLength(200)]]
      }),
      image: this.fb.group({
        name: [''],
        size: [0],
        previewUrl: [''],
        fileId: [''],
        url: [''],
        thumbnailUrl: [''],
        rawFile: [null as File | null]
      }),
      file: this.fb.group({
        name: [''],
        size: [0],
        type: [''],
        fileId: [''],
        url: [''],
        rawFile: [null as File | null]
      }),
      template: this.fb.group({
        title: ['', [Validators.maxLength(60)]],
        subtitle: ['', [Validators.maxLength(100)]],
        buttons: this.fb.array([] as FormGroup[])
      }),
      templateImage: this.fb.group({
        name: [''],
        size: [0],
        previewUrl: [''],
        fileId: [''],
        url: [''],
        rawFile: [null as File | null]
      })
    });
  }

  private createTemplateButtonForm(button?: TemplateButtonFormValue): FormGroup {
    const form = this.fb.group({
      text: [button?.text || '', [Validators.required, Validators.maxLength(20)]],
      action: [button?.action || 'link', [Validators.required]],
      url: [button?.url || ''],
      leadReasonMode: [button?.leadReasonMode || 'create'],
      leadReasonId: [button?.leadReasonId || ''],
      leadReasonName: [button?.leadReasonName || ''],
      leadReasonSourceType: [button?.leadReasonSourceType || null],
      leadReasonDescription: [button?.leadReasonDescription || ''],
      leadReasonBizId: [button?.leadReasonBizId || ''],
      leadReasonValidMode: [button?.leadReasonValidMode || 'permanent'],
      leadReasonValidUntil: [button?.leadReasonValidUntil || null]
    });
    this.syncTemplateButtonValidators(form);
    return form;
  }

  private syncTemplateButtonValidators(button: FormGroup): void {
    const action = String(button.get('action')?.value || 'link');
    const urlControl = button.get('url');
    const leadReasonIdControl = button.get('leadReasonId');
    if (action === 'leadReason') {
      urlControl?.setValidators([]);
      leadReasonIdControl?.setValidators([Validators.required]);
    } else {
      urlControl?.setValidators([Validators.required, Validators.pattern(HTTP_URL_PATTERN)]);
      leadReasonIdControl?.setValidators([]);
    }
    urlControl?.updateValueAndValidity({ emitEvent: false });
    leadReasonIdControl?.updateValueAndValidity({ emitEvent: false });
  }

  private updateSelectedLabelsAndEstimate(labelIds: string[]): void {
    if (!this.requiresCustomerLabels) {
      this.estimatedRecipients = 0;
      this.updateCanSend();
      this.cdr.markForCheck();
      return;
    }
    if (labelIds.length === 0) {
      this.estimatedRecipients = 0;
      this.updateCanSend();
      this.cdr.markForCheck();
      return;
    }
    this.estimateRecipientsSub?.unsubscribe();
    this.estimateRecipientsSub = this.personalBroadcastService
      .estimateRecipients(this.tenantId, labelIds, this.labelMatchMode, this.serviceNumberId)
      .subscribe(count => {
        this.estimatedRecipients = count;
        this.updateCanSend();
        this.cdr.markForCheck();
      });
    this.subscriptions.add(this.estimateRecipientsSub);
  }

  private loadCustomerLabels(reset: boolean): void {
    if (reset) {
      this.labelOptions = [];
      this.labelPageIndex = 0;
      this.labelHasNext = true;
      this.labelLoading = true;
    } else {
      this.labelLoadingMore = true;
    }
    this.cdr.markForCheck();
    const keyword = String(this.labelSearchControl.value || '').trim();
    const request = {
      tenantId: this.tenantId,
      serviceNumberId: this.serviceNumberId || undefined,
      pageIndex: this.labelPageIndex,
      pageSize: this.labelPageSize,
      refreshTime: this.labelRefreshTime,
      key: keyword || undefined
    };
    this.subscriptions.add(
      this.personalBroadcastService.listCustomerLabels(request).subscribe({
        next: (res: CustomerLabelListResult) => {
          if (reset) this.labelRefreshTime = res.refreshTime || this.labelRefreshTime;
          this.labelHasNext = Boolean(res.hasNextPage);
          this.labelPageIndex = Number(res.pageIndex || this.labelPageIndex) + 1;
          const unique = new Map<string, CustomerLabelOption>();
          (reset ? [] : this.labelOptions).forEach(item => unique.set(item.id, item));
          (res.items || []).forEach(item => unique.set(item.id, item));
          this.labelOptions = Array.from(unique.values());
          this.labelLoading = false;
          this.labelLoadingMore = false;
          this.cdr.markForCheck();
        },
        error: () => {
          this.labelLoading = false;
          this.labelLoadingMore = false;
          if (reset) this.labelOptions = [];
          this.message.error(this.i18n.fanyi('personal-broadcast.detail.targets.load-tags-failed'));
          this.cdr.markForCheck();
        }
      })
    );
  }

  submit(): Promise<void> {
    if (this.submitting) return Promise.resolve();

    return new Promise<void>(resolve => {
      this.form.markAllAsTouched();
      this.form.updateValueAndValidity();
      this.cdr.markForCheck();

      if (this.form.invalid) {
        this.message.error(this.i18n.fanyi('personal-broadcast.detail.validation.form-invalid'));
        resolve();
        return;
      }

      const messageEnabled = this.messageEnabled;
      if (messageEnabled) {
        const invalidMessageIndex = this.validateMessages();
        if (invalidMessageIndex >= 0) {
          this.message.error(this.i18n.fanyi('personal-broadcast.detail.validation.complete-message', { index: invalidMessageIndex + 1 }));
          resolve();
          return;
        }
      }

      const sendMode = String(this.form.get('sendMode')?.value || 'immediate') as 'immediate' | 'scheduled';
      const sendAtValue = this.form.get('sendAt')?.value as Date | null;
      const labelIds = this.getBroadcastLabelIds((this.form.get('labelIds')?.value || []) as string[]);
      const channel = this.getBroadcastChannel(String(this.form.get('channel')?.value || ''));

      this.submitting = true;
      this.updateCanSend();
      this.cdr.markForCheck();

      const uploadTasks = messageEnabled
        ? (this.messagesArray.controls
            .map(grp => {
              const type = String(grp.get('type')?.value || 'text') as 'text' | 'image' | 'file' | 'template';
              if (type === 'image') {
                const fileId = String(grp.get(['image', 'fileId'])?.value || '').trim();
                const rawFile = grp.get(['image', 'rawFile'])?.value as File | null;
                if (fileId || !rawFile) return null;
                return { grp, target: 'image' as const, messageType: 'Image' as const, file: rawFile };
              }
              if (type === 'file') {
                const fileId = String(grp.get(['file', 'fileId'])?.value || '').trim();
                const rawFile = grp.get(['file', 'rawFile'])?.value as File | null;
                if (fileId || !rawFile) return null;
                return { grp, target: 'file' as const, messageType: 'File' as const, file: rawFile };
              }
              if (type === 'template') {
                const fileId = String(grp.get(['templateImage', 'fileId'])?.value || '').trim();
                const rawFile = grp.get(['templateImage', 'rawFile'])?.value as File | null;
                if (fileId || !rawFile) return null;
                return { grp, target: 'templateImage' as const, messageType: 'Image' as const, file: rawFile };
              }
              return null;
            })
            .filter(Boolean) as Array<{
            grp: FormGroup;
            target: 'image' | 'file' | 'templateImage';
            messageType: 'File' | 'Image';
            file: File;
          }>)
        : [];

      const upload$ = uploadTasks.length
        ? forkJoin(
            uploadTasks.map(task =>
              this.personalBroadcastService
                .uploadBroadcastAttachment(task.messageType, task.file)
                .pipe(map(result => ({ ...task, result })))
            )
          )
        : of([]);

      this.subscriptions.add(
        upload$
          .pipe(
            map(results => {
              results.forEach(item => {
                item.grp.get([item.target, 'fileId'])?.setValue(item.result.fileId, { emitEvent: false });
                if (item.target === 'image') {
                  item.grp.get([item.target, 'url'])?.setValue(item.result.url || '', { emitEvent: false });
                  item.grp.get([item.target, 'thumbnailUrl'])?.setValue(item.result.thumbnailUrl || '', { emitEvent: false });
                } else if (item.target === 'file') {
                  item.grp.get([item.target, 'url'])?.setValue(item.result.url || '', { emitEvent: false });
                } else if (item.target === 'templateImage') {
                  item.grp.get([item.target, 'url'])?.setValue(item.result.url || item.result.fileId, { emitEvent: false });
                }
                item.grp.get([item.target, 'rawFile'])?.setValue(null, { emitEvent: false });
              });
              return results;
            }),
            switchMap(() => {
              const name = String(this.form.get('title')?.value || '').trim();
              const remark = String(this.form.get('remark')?.value || '').trim();
              const payload: PersonalBroadcastPayload = {
                tenantId: this.tenantId,
                serviceNumberId: this.serviceNumberId || undefined,
                name,
                remark: remark || undefined,
                channel,
                labelIds,
                matchLogic: this.labelMatchMode === 'and' ? MatchLogic.And : MatchLogic.Or,
                sendMode,
                sendAt: sendMode === 'scheduled' && sendAtValue ? new Date(sendAtValue).toISOString() : null,
                messages: messageEnabled
                  ? this.messagesArray.controls.map(grp => {
                      const type = String(grp.get('type')?.value || 'text') as 'text' | 'image' | 'file' | 'template';
                      if (type === 'text') {
                        return { type, content: String(grp.get(['text', 'content'])?.value || '').trim() };
                      }
                      if (type === 'image') {
                        return {
                          type,
                          name: String(grp.get(['image', 'name'])?.value || '').trim(),
                          size: Number(grp.get(['image', 'size'])?.value || 0),
                          fileId: String(grp.get(['image', 'fileId'])?.value || '').trim(),
                          url: String(grp.get(['image', 'url'])?.value || '').trim() || undefined,
                          thumbnailUrl: String(grp.get(['image', 'thumbnailUrl'])?.value || '').trim() || undefined
                        };
                      }
                      if (type === 'file') {
                        return {
                          type,
                          name: String(grp.get(['file', 'name'])?.value || '').trim(),
                          size: Number(grp.get(['file', 'size'])?.value || 0),
                          mimeType: String(grp.get(['file', 'type'])?.value || ''),
                          fileId: String(grp.get(['file', 'fileId'])?.value || '').trim(),
                          url: String(grp.get(['file', 'url'])?.value || '').trim() || undefined
                        };
                      }
                      const buttons = grp.get(['template', 'buttons']) as FormArray<FormGroup> | null;
                      return {
                        type,
                        title: String(grp.get(['template', 'title'])?.value || '').trim(),
                        subtitle: String(grp.get(['template', 'subtitle'])?.value || '').trim(),
                        imageUrl:
                          String(grp.get(['templateImage', 'url'])?.value || '').trim() ||
                          String(grp.get(['templateImage', 'fileId'])?.value || '').trim(),
                        buttons: (buttons?.controls || []).map(btn => {
                          const text = String(btn.get('text')?.value || '').trim();
                          const action = String(btn.get('action')?.value || 'link') as 'link' | 'leadReason';
                          if (action === 'leadReason') {
                            const leadReasonId = String(btn.get('leadReasonId')?.value || '');
                            const leadReasonName =
                              this.leadReasonCache.get(leadReasonId)?.name ||
                              this.leadReasonOptions.find(item => item.id === leadReasonId)?.name ||
                              String(btn.get('leadReasonName')?.value || '').trim();
                            return { text, action: 'leadReason', leadReasonId, leadReasonName };
                          }
                          return { text, action: 'link', url: String(btn.get('url')?.value || '') };
                        })
                      };
                    })
                  : []
              };
              return this.isCreate
                ? this.personalBroadcastService.createOrSendBroadcast(payload)
                : this.personalBroadcastService.updateBroadcast(this.taskId, payload);
            }),
            finalize(() => {
              this.submitting = false;
              this.updateCanSend();
              this.cdr.markForCheck();
            })
          )
          .subscribe({
            next: () => {
              if (this.isCreate) {
                this.message.success(
                  this.i18n.fanyi(
                    sendMode === 'scheduled'
                      ? 'personal-broadcast.detail.submit.success.create.scheduled'
                      : 'personal-broadcast.detail.submit.success.create.immediate'
                  )
                );
              } else {
                this.message.success(this.i18n.fanyi('personal-broadcast.detail.submit.success.update'));
              }
              this.back();
              resolve();
            },
            error: (err: any) => {
              const msg = String(err?.message || '').trim();
              this.message.error(
                msg
                  ? this.i18n.fanyi('personal-broadcast.detail.submit.failed-with-msg', { msg })
                  : this.i18n.fanyi('personal-broadcast.detail.submit.failed')
              );
              resolve();
            }
          })
      );
    });
  }

  private validateMessages(): number {
    for (let i = 0; i < this.messagesArray.controls.length; i += 1) {
      const grp = this.messagesArray.at(i);
      const type = String(grp.get('type')?.value || 'text');
      if (type === 'text') {
        const content = String(grp.get(['text', 'content'])?.value || '').trim();
        if (!content) return i;
      } else if (type === 'template') {
        const title = String(grp.get(['template', 'title'])?.value || '').trim();
        if (!title) return i;
        const fileId = String(grp.get(['templateImage', 'fileId'])?.value || '').trim();
        const rawFile = grp.get(['templateImage', 'rawFile'])?.value as File | null;
        const url = String(grp.get(['templateImage', 'url'])?.value || '').trim();
        const previewUrl = String(grp.get(['templateImage', 'previewUrl'])?.value || '').trim();
        if (!fileId && !rawFile && !url && !previewUrl) return i;
        const buttons = grp.get(['template', 'buttons']) as FormArray<FormGroup> | null;
        const buttonControls = buttons?.controls || [];
        if (buttonControls.length < MIN_TEMPLATE_BUTTONS || buttonControls.length > MAX_TEMPLATE_BUTTONS) return i;
        for (const btn of buttonControls) {
          const text = String(btn.get('text')?.value || '').trim();
          const action = String(btn.get('action')?.value || 'link');
          if (!text) return i;
          if (action === 'leadReason') {
            const leadReasonId = String(btn.get('leadReasonId')?.value || '').trim();
            if (!leadReasonId) return i;
          } else {
            const buttonUrl = String(btn.get('url')?.value || '').trim();
            if (!buttonUrl || !HTTP_URL_PATTERN.test(buttonUrl)) return i;
          }
        }
      } else if (type === 'image') {
        const fileId = String(grp.get(['image', 'fileId'])?.value || '').trim();
        const rawFile = grp.get(['image', 'rawFile'])?.value as File | null;
        const url = String(grp.get(['image', 'url'])?.value || '').trim();
        const thumbnailUrl = String(grp.get(['image', 'thumbnailUrl'])?.value || '').trim();
        const previewUrl = String(grp.get(['image', 'previewUrl'])?.value || '').trim();
        if (!fileId && !rawFile && !url && !thumbnailUrl && !previewUrl) return i;
      } else if (type === 'file') {
        const name = String(grp.get(['file', 'name'])?.value || '').trim();
        const fileId = String(grp.get(['file', 'fileId'])?.value || '').trim();
        const rawFile = grp.get(['file', 'rawFile'])?.value as File | null;
        const url = String(grp.get(['file', 'url'])?.value || '').trim();
        if (!name && !url) return i;
        if (!fileId && !rawFile && !url) return i;
      }
    }
    return -1;
  }
}
